import { Table, Checkbox, Button, Space, Input, Select, Form } from "antd";
import { useState } from "react";
import styles from "./WordsList.module.css";
import type { ColumnsType, ColumnType } from "antd/es/table";
import { Word } from "../../../types/words";
import { FileOutlined } from "@ant-design/icons";
import { CRITERIA_OPTIONS } from "../../../utils/constants";
import { CriteriaOption } from "../../../types/task";
import { useMessageContext } from "../../../providers";
import WordsActionRow from "./WordsActionRow/WordsActionRow";
import CriteriaTag from "../../../components/Tag/CriteriaTag";

interface EditableColumnType extends ColumnType<Word> {
  editable?: boolean;
}

interface EditableCellProps extends React.HTMLAttributes<HTMLElement> {
  editing: boolean;
  dataIndex: string;
  title: string;
  inputType: 'text' | 'select';
  record: Word;
  index: number;
  children: React.ReactNode;
}

const EditableCell: React.FC<EditableCellProps> = ({
  editing,
  dataIndex,
  title,
  inputType,
  record,
  index,
  children,
  ...restProps
}) => {
  const inputNode = inputType === 'select' ? (
    <Select
      mode="multiple"
      options={CRITERIA_OPTIONS}
      placeholder="Selecciona criteris"
      style={{ width: '100%' }}
    />
  ) : <Input placeholder={`Introdueix ${title?.toLowerCase()}`} />;

  return (
    <td {...restProps}>
      {editing ? (
        <Form.Item
          name={dataIndex}
          style={{ margin: 0 }}
          rules={
            dataIndex === 'additional_info'
              ? []
              : [{ required: true, message: 'Aquest camp és obligatori.' }]
          }
        >
          {inputNode}
        </Form.Item>
      ) : (
        children
      )}
    </td>
  );
};

interface WordsListProps {
  words: Word[];
  loading?: boolean;
  onWordUpdate?: (word: Word) => void;
  onWordDelete?: (wordKey: string) => void;
  criteriaFilter: string[];
  onCriteriaFilterChange: (filter: string[]) => void;
}

const WordsList = ({ words, loading = false, onWordUpdate, onWordDelete, criteriaFilter, onCriteriaFilterChange }: WordsListProps) => {
  const [editingKey, setEditingKey] = useState<string>('');
  const [editingWord, setEditingWord] = useState<Word | null>(null);
  const { sendMessage } = useMessageContext();
  const [form] = Form.useForm();

  const isEditing = (record: Word) => record.id === editingKey;

  const edit = (record: Word) => {
    setEditingKey(record.id);
    setEditingWord({ ...record });
    form.setFieldsValue({
      word: record.word,
      additional_info: record.additional_info,
      criteria: record.criteria
    });
  };

  const cancel = () => {
    setEditingKey('');
    setEditingWord(null);
  };

  const validateWord = (word: Word): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];
    
    if (!word.word || word.word.trim() === '') {
      errors.push('Aquest camp és obligatori.');
    }
    
    if (!word.criteria || word.criteria.length === 0) {
      errors.push('Aquest camp és obligatori.');
    }
    
    if (word.word && word.word.trim() !== '') {
      const wordExists = words.some(existingWord => 
        existingWord.id !== word.id && 
        existingWord.word.toLowerCase() === word.word.toLowerCase()
      );
      
      if (wordExists) {
        errors.push('Error al editar paraula.Aquesta paraula ja existeix a la llista blanca.');
      }
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  };

  const save = async () => {
    if (editingWord) {
      try {
        const values = await form.validateFields();
        
        const wordExists = words.some(existingWord => 
          existingWord.id !== editingWord.id && 
          existingWord.word.toLowerCase() === values.word.toLowerCase()
        );
        
        if (wordExists) {
          sendMessage('Error al editar paraula. Aquesta paraula ja existeix a la llista blanca.', "error", 3);
          return;
        }
        
        const updatedWord = {
          ...editingWord,
          word: values.word,
          additional_info: values.additional_info,
          criteria: values.criteria
        };
        
        onWordUpdate?.(updatedWord);
        setEditingKey('');
        setEditingWord(null);
      } catch (errorInfo) {
        console.log('Validate Failed:', errorInfo);
      }
    }
  };

  const columns: EditableColumnType[] = [
    {
      title: "Paraula",
      dataIndex: "word",
      key: "word",
      sorter: (a, b) => a.word.localeCompare(b.word),
      ellipsis: true,
      width: 250,
      editable: true,
    },
    {
      title: "Informació addicional",
      dataIndex: "additional_info",
      key: "additional_info",
      sorter: (a, b) => a.additional_info?.localeCompare(b.additional_info || "") || 0,
      width: 300,
      ellipsis: true,
      editable: true,
    },
    {
      title: "Criteris",
      dataIndex: "criteria",
      key: "criteria",
      render: (criteria: CriteriaOption[]) => (
        <>
          {criteria?.map((criteri: CriteriaOption, index: number) => (
            <CriteriaTag key={criteri} criteria={criteri as CriteriaOption} />
          ))}
        </>
      ),
      sorter: (a, b) =>
        a.criteria?.join(", ").localeCompare(b.criteria?.join(", ")),
      filteredValue: criteriaFilter,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <div style={{ marginBottom: 8 }}>
            <Checkbox.Group
              options={CRITERIA_OPTIONS.map(option => ({
                label: option.label,
                value: option.value
              }))}
              value={selectedKeys as string[]}
              onChange={(checkedValues) => setSelectedKeys(checkedValues)}
              style={{ display: 'flex', flexDirection: 'column', gap: 4 }}
            />
          </div>
          <Space>
            <Button
              onClick={() => {
                clearFilters?.();
                onCriteriaFilterChange([]);
                confirm();
              }}
              size="small"
              style={{ width: 90 }}
            >
              Cancel·lar
            </Button>
            <Button
              type="primary"
              onClick={() => {
                onCriteriaFilterChange(selectedKeys as string[]);
                confirm();
              }}
              size="small"
              style={{ width: 90 }}
            >
              Aplicar
            </Button>
          </Space>
        </div>
      ),
      onFilter: (value, record) => {
        if (!criteriaFilter || criteriaFilter.length === 0) return true;
        if (!record.criteria || record.criteria.length === 0) return false;
        return criteriaFilter.some(selectedCriteri => 
          record.criteria?.includes(selectedCriteri as CriteriaOption)
        );
      },
      width: 400,
      editable: true,
    },
    {
      title: "Accions",
      render: (_, word) => {
        const isWordValid = editingWord ? validateWord(editingWord).isValid : true;
        return (
          <WordsActionRow 
            word={word} 
            isEditing={isEditing(word)}
            isWordValid={isWordValid}
            onEdit={() => edit(word)}
            onSave={save}
            onCancel={cancel}
            onDelete={onWordDelete}
          />
        );
      },
      fixed: "right",
      width: 150,
    },
  ];

  const mergedColumns = columns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: Word) => ({
        record,
        inputType: col.dataIndex === 'criteria' ? 'select' : 'text',
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
      }),
    };
  });

  const customLocale = {
    emptyText: (
      <div style={{ textAlign: "center", margin: 40 }} className={styles.emptyContainer}>
        <FileOutlined style={{ fontSize: 48, color: "#ccc", marginBottom: 15 }} className={styles.emptyIcon}/>
        <div>La teva llista blanca es troba buida. Pots afegir-ne des del “Panell de creació de paraules”.</div>
      </div>
    ),
    triggerDesc: "Clic per ordenar descendent",
    triggerAsc: "Clic per ordenar ascendent",
    cancelSort: "Cancel·lar ordre",
  };

  return (
    <Form form={form} component={false}>
      <Table 
        components={{
          body: { cell: EditableCell },
        }}
        dataSource={words} 
        columns={mergedColumns as ColumnsType<Word>} 
        locale={customLocale} 
        pagination={{ pageSize: 8 }} 
        loading={loading}
        rowKey="key"
      />
    </Form>
  );
};

export default WordsList;